import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { Clock, MessageSquare, Bot, Percent } from "lucide-react";
import { useQ } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { PageHeader, Panel, Stat } from "@/components/common";

const tooltip = { contentStyle: { background: "#111118", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 } };
const axis = { stroke: "#475569", fontSize: 11, tickLine: false, axisLine: false };

export default function Analytics() {
  const { t } = useT();
  const { data } = useQ("/analytics");
  if (!data) return <div className="h-64 rounded-lg bg-white/[0.03] animate-pulse" />;
  const series = data.series.map((d) => ({ ...d, label: d.date.slice(5) }));
  const tt = data.totals;
  return (
    <div>
      <PageHeader eyebrow={t("nav_analytics")} title={t("analytics_title")} subtitle={t("analytics_sub")} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat label={t("hours_streamed")} value={tt.hours_streamed} icon={Clock} testId="an-hours" />
        <Stat label={t("msgs_received")} value={tt.messages_received} icon={MessageSquare} testId="an-received" delay={60} />
        <Stat label={t("ai_responses")} value={tt.ai_responses} icon={Bot} testId="an-ai" accent="text-indigo-300" delay={120} />
        <Stat label={t("response_rate")} value={`${tt.response_rate}%`} icon={Percent} testId="an-rate" accent="text-emerald-400" delay={180} />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Panel className="lg:col-span-2" data-testid="chart-messages">
          <p className="eyebrow mb-6">{t("chart_messages")}</p>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={series}>
              <defs>
                <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366F1" stopOpacity={0.5} /><stop offset="100%" stopColor="#6366F1" stopOpacity={0} /></linearGradient>
                <linearGradient id="gR" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E11D48" stopOpacity={0.35} /><stop offset="100%" stopColor="#E11D48" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="label" {...axis} /><YAxis {...axis} allowDecimals={false} />
              <Tooltip {...tooltip} /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="received" name={t("msgs_received")} stroke="#E11D48" fill="url(#gR)" strokeWidth={2} />
              <Area type="monotone" dataKey="answered" name={t("msgs_answered")} stroke="#6366F1" fill="url(#gA)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
        <Panel data-testid="chart-minutes">
          <p className="eyebrow mb-6">{t("chart_minutes")}</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={series}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="label" {...axis} /><YAxis {...axis} allowDecimals={false} />
              <Tooltip {...tooltip} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="minutes" name={t("minutes")} fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>
    </div>
  );
}
